import { Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSetupAction } from '@/hooks/useSetup';
import type { SetupState } from '@/types';
import { doneCount, type StepConfig } from './steps';

/**
 * The bottom-right pill the rail collapses to. Names the next step so wandering
 * off the tour never loses the thread.
 */
export function SetupChip({
  state,
  steps,
  next,
  onGo
}: {
  state: SetupState;
  steps: StepConfig[];
  next: StepConfig | undefined;
  /** Navigates to the next step's route and expands the rail. */
  onGo: () => void;
}) {
  const action = useSetupAction();
  const done = doneCount(steps, state);
  return (
    <div
      role="status"
      className="fixed right-4 bottom-4 z-40 flex max-w-[calc(100vw-2rem)] items-center gap-2 rounded-full border border-[var(--line)] bg-[var(--surface)] py-1.5 pr-1.5 pl-4 text-sm shadow-lg"
    >
      <span className="min-w-0 truncate">
        <span className="font-medium">Setup</span>
        <span className="text-subtle"> · </span>
        <span className="tnum text-muted">
          {done} of {steps.length}
        </span>
        {next ? (
          <>
            <span className="text-subtle"> · </span>
            <span className="text-muted">Next: {next.title}</span>
          </>
        ) : null}
      </span>
      <Button variant="primary" size="sm" className="h-7 rounded-full px-3" onClick={onGo}>
        {next ? 'Go' : 'Open'}
      </Button>
      <Button
        variant="ghost"
        size="iconSm"
        className="size-7 rounded-full"
        aria-label="Hide setup"
        title="Hide setup"
        onClick={() => action.mutate('hide')}
        disabled={action.isPending}
      >
        <X />
      </Button>
    </div>
  );
}

/**
 * The one-time end of onboarding, in the chip's place: shown when the last
 * step is done, then gone with the rest of the guide.
 */
export function SetupDoneChip({ onClose }: { onClose: () => void }) {
  return (
    <div
      role="status"
      className="fixed right-4 bottom-4 z-40 flex max-w-[calc(100vw-2rem)] items-center gap-2.5 rounded-full border border-[var(--line)] bg-[var(--surface)] py-1.5 pr-1.5 pl-2 text-sm shadow-lg"
    >
      <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-[var(--accent)] text-[var(--accent-fg)]">
        <Check className="size-3.5" />
      </span>
      <span className="font-medium">You’re set up.</span>
      <Button
        variant="ghost"
        size="iconSm"
        className="size-7 rounded-full"
        aria-label="Close"
        onClick={onClose}
      >
        <X />
      </Button>
    </div>
  );
}
